/**
 * ImportPreviewShell — casca padronizada para diálogos de "preview de importação".
 *
 * Fornece header, KPIs, bloco opcional de variáveis e área de tabela rolável,
 * com layout responsivo (fullscreen no mobile, centralizado no desktop) e
 * footer com aviso de mascaramento server-side.
 *
 * RELEASE:
 *   2026-05-17 v1.0.0 - Versão inicial (extraída de ListPreviewDialog).
 */
import { ReactNode } from 'react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Calendar, Hash, Copy, ShieldCheck } from 'lucide-react';
import { cn } from '@/lib/utils';

interface ImportPreviewShellProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Ícone exibido à esquerda do título no header. */
  headerIcon: ReactNode;
  title: string;
  /** Hash/código curto a exibir (opcional). */
  hashCode?: string;
  /** Data ISO exibida no subheader (opcional). */
  createdAt?: string;
  /** KPIs renderizados no topo do corpo. */
  metrics: ReactNode;
  /** Bloco opcional de variáveis (chips). */
  variablesBlock?: ReactNode;
  /** Área da tabela. */
  children: ReactNode;
  /** Texto opcional para o rodapé (override). */
  footerNote?: string;
}

function formatDateTime(iso: string) {
  return new Date(iso).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function truncateHash(h: string) {
  return (h ?? '').slice(0, 8).toUpperCase();
}

export function ImportPreviewShell({
  open,
  onOpenChange,
  headerIcon,
  title,
  hashCode,
  createdAt,
  metrics,
  variablesBlock,
  children,
  footerNote,
}: ImportPreviewShellProps) {
  const copyHash = async () => {
    if (!hashCode) return;
    try {
      await navigator.clipboard.writeText(hashCode);
    } catch {
      /* silencioso */
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn(
          'p-0 gap-0 overflow-hidden flex flex-col',
          'w-screen h-[100dvh] max-w-none rounded-none',
          'sm:w-[95vw] sm:h-[88vh] sm:max-w-5xl sm:rounded-lg',
        )}
      >
        {/* HEADER */}
        <div className="flex items-start justify-between gap-3 px-5 py-4 border-b bg-background shrink-0">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 mb-1">
              <div className="h-9 w-9 rounded-md bg-primary/10 text-primary flex items-center justify-center shrink-0">
                {headerIcon}
              </div>
              <div className="min-w-0 flex-1">
                <h2 className="text-base sm:text-lg font-semibold truncate leading-tight">
                  {title}
                </h2>
                {(hashCode || createdAt) && (
                  <div className="flex items-center gap-2 text-xs text-muted-foreground mt-0.5">
                    {hashCode && (
                      <>
                        <Hash className="h-3 w-3" />
                        <button
                          type="button"
                          onClick={copyHash}
                          className="font-mono hover:text-foreground transition-colors inline-flex items-center gap-1"
                          title="Copiar código completo"
                        >
                          {truncateHash(hashCode)}
                          <Copy className="h-3 w-3 opacity-60" />
                        </button>
                      </>
                    )}
                    {hashCode && createdAt && <span className="opacity-40">•</span>}
                    {createdAt && (
                      <>
                        <Calendar className="h-3 w-3" />
                        <span>{formatDateTime(createdAt)}</span>
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* CORPO */}
        <div className="flex-1 min-h-0 overflow-auto px-5 py-4 space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">{metrics}</div>
          {variablesBlock}
          {children}
        </div>

        {/* FOOTER */}
        <div className="shrink-0 px-5 py-3 border-t bg-muted/30 flex items-center justify-between gap-3">
          <p className="text-[11px] text-muted-foreground flex items-center gap-1.5">
            <ShieldCheck className="h-3 w-3 text-emerald-600 shrink-0" />
            {footerNote ??
              'Exibindo até 50 registros (amostra). Dados sensíveis são mascarados pelo servidor.'}
          </p>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-7 text-xs"
            onClick={() => onOpenChange(false)}
          >
            Fechar
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
